import React from "react"
import { graphql, useStaticQuery } from "gatsby"
import { useFlexSearch } from "react-use-flexsearch"
import Section from "../ui/Section/Section"
import ProductListingView from "./ProductListing/ProductListingView"
import Filter, { IFilter } from "./ProductListing/Filter";
import * as styles from './ProductListing.module.scss';
import classnames from 'classnames';
import Questions from "../blocks/Questions"
import GraphicDesign from "../blocks/GraphicDesign"
import { Breakpoints } from "../../utils/Types"
import useBreakpoint from "../../utils/useBreakpoint"
import SearchIcon from "../../assets/search.icon.svg";
import useGdprIsAllowed from "../Gdpr/useGdprIsAllowed"

type ListElement = Array<{
    element: React.ReactElement, 
    colspan: number,
    key: string,
}>;

type InlineBreakerElements = Array<{element: React.ReactElement, colspan?: number, position: number | Array<number>}>;

type Props = {
    hasFilters?: boolean
    filterByType?: string
    inlineBreaker?: InlineBreakerElements
}
export default (props: Props) => {
    const { hasFilters, filterByType } = props

    const columnSizeInNumber: number =  (hasFilters) ? 4 : 3;

    const data = useStaticQuery(graphql`{
  allProdukteHJson {
    edges {
      node {
        weight
        kurzbeschreibung
        listenbild {
          alt
          src {
            childImageSharp {
              gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH)
            }
          }
        }
        name
        produktTyp
        url
        filters {
          typ
          wert
          filter
          widget
          widgetType
        }
      }
    }
  }
  localSearchArtikel {
    index
    store
  }
}
`);
    const trackingAllowed = useGdprIsAllowed("Analyse");
    const timeout = React.useRef(null);
    const [search, setSearch] = React.useState<string>("");
    const [filterSelection, setFilterSelection]: [{[key: string]: Array<string>}, Function] = React.useState({});

    const results = useFlexSearch(
        search,
        data.localSearchArtikel.index,
        data.localSearchArtikel.store,
    )

    // Inline Breakers
    const inlineBreaks: InlineBreakerElements = props.inlineBreaker ? props.inlineBreaker : [];

    // Listing Breakers
    const listingBreaksElements = [<Questions />, <GraphicDesign />]
    const listingBreaksRows: {[keys in Breakpoints]: number} = {
        mobile: 1,
        tablet: 2,
        desktop: 2,
    }
        
    const breakpoint = useBreakpoint();
    const [linesTillBreak, setLinesTillBreak] = React.useState(2);

    // Pagination
    const [page, setPage] = React.useState(0);
    const [itemsPerPage, setItemsPerPage] = React.useState(2);
    const itemsPerPageBreakpoints: {[keys in Breakpoints]: number} = {
        mobile: 12,
        tablet: 24,
        desktop: 24,
    }

    React.useEffect(() => {
        if (breakpoint) {
            setLinesTillBreak(listingBreaksRows[breakpoint]);
            setItemsPerPage(itemsPerPageBreakpoints[breakpoint]);
        }
    }, [breakpoint]);


    // Prefilter products on filterByType given by props.
    // this speeds up filter extraction and other operations with the array.
    const products = data.allProdukteHJson.edges.filter(({ node }) => {
        if (!filterByType) {
            return true;
        }
        // Filter by types given by props.
        if (
            filterByType &&
            node.produktTyp === filterByType
        ) {
            return true
        }
        return false
    }).sort((a, b) => {
        if (a.node.weight < b.node.weight) {
            return 1;
        }
        if (a.node.weight > b.node.weight) {
            return -1;
        }
        return 0;
    });


    let filters: {[key: string ]: IFilter} = {};

    // Extract filter configs
    if (hasFilters) {
        for (const p of products) {
            if (!p.node.filters) {
                continue;
            }

            for (const filterValue of p.node.filters) {
                if (filterValue.filter === false) {
                    continue;
                }

                if (!filters.hasOwnProperty(filterValue.typ)) {
                    filters[filterValue.typ] = { type: filterValue.typ, options: []};
                }

                const werte: Array<string> = filterValue.wert.split("|");

                for (const wert of werte) {
                    let foundOption: boolean = false;

                    for (const option of filters[filterValue.typ].options) {
                        if (option.value === wert.toLowerCase()) {
                            foundOption = true;
                            continue;
                        }
                    }

                    if (!foundOption) {
                        filters[filterValue.typ].options.push({name: wert, value: wert.toLowerCase()});
                    }
                }

            }
        }
    }

        // filter products by search string
    const productsToDisplay = products.filter(({ node }) => {
        if (!search || search === "") {
            return true
        }

        for (const result of results) {
            if (result.url === node.url) {
                return true
            }
        }
        return false
    })
    // Filter products by filter selection
    .filter(({node}) => {
        let isIn: boolean = true;
        let foundType: boolean = false;

        for (const filterName in filterSelection) {
            if (filterSelection[filterName].length === 0) {
                continue;
            }

            for (const nodeFilters of node.filters) {
                if (nodeFilters.typ === filterName) {
                    foundType = true;

                    const werte: Array<string> = nodeFilters.wert.split("|");
                    let match: boolean = false;
                    for (const wert of werte) {
                        if (filterSelection[filterName].indexOf(wert.toLowerCase()) > -1) {
                            match = true;
                        }
                    }

                    if (!match) {
                        isIn = false;
                    }
                }
            }

            if (!foundType) {
                isIn = false;
            }
        }

        return isIn;
    });


    // Mix items and inlineBreakers
    let elementsArray: ListElement = [];
    let elementsArrayLength: number = 0;
    const addInlineBreaker = () => {
        inlineBreaks.forEach((inlineBreaker, key) => {
            if ((Array.isArray(inlineBreaker.position) && inlineBreaker.position.indexOf(elementsArrayLength) >= 0) || inlineBreaker.position === elementsArrayLength ) {
                elementsArray.push({
                    colspan: inlineBreaker.colspan ? inlineBreaker.colspan : 1,
                    element: inlineBreaker.element,
                    key: "breaker" + key,
                });
                elementsArrayLength += inlineBreaker.colspan ? inlineBreaker.colspan : 1;
            }
        });
    }
    productsToDisplay.forEach((item) => {
        // add breaker if index matches a position
        addInlineBreaker();

        // Add product
        elementsArray.push({
            colspan: 1,
            element: <ProductListingView item={item.node} />,
            key: item.node.url,
        });
        elementsArrayLength += 1;
    });
    // Check if on last Element a inlineBreaker could have place
    addInlineBreaker();

    // PAGINATION
    // number of pages extraction
    const pagesArray = [];
    for (let i = 0; i < Math.ceil(elementsArrayLength/itemsPerPage); i++) {
        pagesArray.push(i);
    }

    // pagination items
    const productsToDisplayPage: ListElement = [];
    let indexWithSpan: number = 0;
    elementsArray.forEach((item, index) => {
        const pageStart = itemsPerPage * page;
        const pageEnd = pageStart + itemsPerPage;

        if (indexWithSpan >= pageStart && indexWithSpan < pageEnd) {
            productsToDisplayPage.push(item);
        }

        // update index manually with colspan
        indexWithSpan += item.colspan;
    });

    // Handle filter Change
    const onFilterChange = (type: string, selected: Array<string>) => {
        setPage(0);
        const newFilterSelection = {...{}, ...filterSelection};
        newFilterSelection[type] = selected;
        setFilterSelection(newFilterSelection);
    }

    // Handle search input
    const doSearch = e => {
        setPage(0);
        setSearch(e.target.value)
    }

    // Hande Page Change
    const changePage = (newPage) => {
        setPage(newPage);
    }

    const hasFiltersSet = (): boolean => {
        let hasSet: boolean = false;
        for (const filterKeys in filterSelection) {
            if (filterSelection[filterKeys].length > 0) {
                hasSet = true;
            }
        }

        return hasSet;
    }

    // Track filter and search changes
    React.useEffect(() => {
        if(timeout.current) clearTimeout(timeout.current);
        timeout.current = setTimeout(() => {
            // track on piwik
            if (_paq && trackingAllowed && (search || hasFiltersSet())) {
                  _paq.push(['trackSiteSearch',
                      // Search keyword searched for
                      search + (hasFiltersSet() ? " + Filter: " + Object.keys(filterSelection).map(name => filterSelection[name].length > 0 ? name +": " + filterSelection[name].join(",") : null).join(" ") : " "),
                      // Search category selected in your search engine. If you do not need this, set to false
                      "Produktsuche" + (filterByType ? " " + filterByType : ""),
                      // Number of results on the Search results page. Zero indicates a 'No Result Search Keyword'. Set to false if you don't know
                      productsToDisplay.length
                  ]);
          }
        }, 1000);
    }, [search, filterSelection]);


    let indexWithColspanRenderLoop: number = 0;

    return (
        <>
            <div className={classnames(["container", styles.searchBox])} >
                <div className="columns">
                    <div className="column is-one-fifth is-hidden-mobile"></div>
                    <div className="column">
                        <div className="field has-addons">
                            <div className="control is-expanded has-icons-right">
                                <input
                                    className={classnames(["input", styles.searchInput])}
                                    type="text"
                                    value={search}
                                    onChange={doSearch}
                                    placeholder="Nach was suchen Sie?"
                                />
                                <span className={classnames(["icon", "is-right", styles.searchIconWrapper])}>
                                    <SearchIcon />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="column is-one-fifth is-hidden-mobile"></div>
                </div>
            </div>

            <Section>
                <div className="columns">
                    {hasFilters && <div className="column is-one-quarter is-flex-mobile" style={{flexWrap: "wrap"}}>
                         
                        {/* Filter Logic */}
                        {Object.keys(filters).map(filterName => {
                            const filter = filters[filterName];
                            return <Filter key={filterName} config={filter} onChange={onFilterChange} />;
                        })}


                    </div>}
                    <div className="column">

                        {/* Listing Renders */}

                        <div className="columns is-multiline">
                            {productsToDisplayPage
                                .map((item) => {
                                    const index = indexWithColspanRenderLoop;

                                    const itemsPerRow = ((hasFilters) ? 3 : 4) * linesTillBreak;
                                    let addition = null;
                                    if (index % itemsPerRow  === 0 && index >= itemsPerRow &&  index <= itemsPerRow * listingBreaksElements.length) {
                                            // Add the breaker here
                                            const Element = React.cloneElement(listingBreaksElements[(index/itemsPerRow) - 1], {withFilterMode: hasFilters})
                                            addition = <div key={"listbreaker-" + index} className={classnames(["column", "is-full", styles.columnOutsideWrapper])} >
                                            <div className={(!hasFilters) ? styles.outsideWrapper : styles.outsideWrapperWithFilter}>
                                                {Element}
                                            </div>
                                        </div>
                                    }

                                    // update index and consider colspan
                                    indexWithColspanRenderLoop += item.colspan;

                                    return [addition, <div
                                        className={"column is-" + columnSizeInNumber * item.colspan}
                                        key={item.key}
                                    >
                                        {item.element}
                                    </div>];
                                })}

                            {(productsToDisplay.length === 0) && <div className="content">
                                <p className={classnames(["title", "is-4"])}>Keine Produkte verfügbar</p>
                                <p>Für diese Filterkombination sind leider keine Produkte verfügbar. Bitte reduzieren Sie die Auswahl der Filter</p>
                            </div>}
                        </div>

                    </div>
                </div>
                {pagesArray.length > 1 && <div className="content has-text-right">

                    {page >= 0 && <a href="#" onClick={(e) => {changePage(page - 1)}}>
                        <svg width="13" height="13" viewBox="0 0 13 13" version="1.1" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.094,0.375l6.145,6.027l-6.145,6.092" style={{fill: "none", stroke:"#1B9DDC",strokeWidth: "0.75px", transform: "rotate(180deg) translate(-19px, -13px) "}} />
                        </svg>   
                    </a>}
                    {pagesArray.map(pageNumber => <a 
                        key={"page-"+pageNumber} 
                        href={"#"} 
                        className={classnames([styles.paginationItem], (page === pageNumber) ? styles.paginationItemActive : "")} 
                        onClick={(e) => {changePage(pageNumber)}}
                    > 
                        {(page === pageNumber) ? "Seite " : ""}
                        {pageNumber + 1} 
                    </a>)}

                    {page  !== pagesArray.length - 1 && pagesArray.length >= 2 && <a href="#" onClick={(e) => {changePage(page + 1)}}>
                        <svg width="13" height="13" viewBox="0 0 13 13" version="1.1" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.094,0.375l6.145,6.027l-6.145,6.092" style={{fill: "none", stroke:"#1B9DDC",strokeWidth: "0.75px", transform: "translate(-13px, 0) "}} />
                        </svg>   
                    </a>}
                </div>}
                
            </Section>
        </>
    )
}
